import clsx from "clsx";
import { isEqual } from "lodash";

import { LoadingIndicator } from "@components/v2";
import { BasicTooltip } from "@components/v2/tooltips";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import {
  type QuickFilter,
  type QuickFilterRevenueConfig,
} from "@north-beam/nb-common";

import { DashboardItem } from "./dashboard-item";

interface Props {
  dashboards: QuickFilter<QuickFilterRevenueConfig>[];
  selectedDashboardId: string | null;
  reordering: boolean;
  onSelect: (val: string) => void;
  onReorder: (val: QuickFilter<QuickFilterRevenueConfig>[]) => void;
}

export const Dashboards = ({
  dashboards,
  selectedDashboardId,
  reordering,
  onSelect,
  onReorder,
}: Props) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const oldIndex = dashboards.findIndex((item) => item.id === active.id);
    const newIndex = dashboards.findIndex((item) => item.id === over?.id);
    const newOrdering = arrayMove(dashboards, oldIndex, newIndex);

    if (!isEqual(dashboards, newOrdering)) {
      onReorder(newOrdering);
    }
  };

  return (
    <div className="flex flex-col overflow-hidden text-xs relative gap-2">
      {reordering && (
        <div
          className={clsx(
            "absolute left-0 top-0 right-0 bottom-0 bg-v2-glass-inverse",
            "flex items-center justify-center",
          )}
        >
          <LoadingIndicator />
        </div>
      )}
      <div className="flex text-xs font-semibold ml-2">
        Dashboard Order
        <BasicTooltip
          className="flex items-center"
          title="Ordering"
          content="Drag to reorder dashboard tabs. Click to update or view."
        />
      </div>
      <div className="overflow-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToParentElement, restrictToVerticalAxis]}
        >
          <SortableContext items={dashboards.map((e) => e.id)}>
            {dashboards.map((item) => (
              <DashboardItem
                key={item.id}
                id={item.id}
                active={item.id === selectedDashboardId}
                title={item.name}
                onClick={() => onSelect(item.id)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
